'use client'

import { useMemo, useState, type FC } from 'react'
import dynamic from 'next/dynamic'
import DOMPurify from 'dompurify'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Label } from 'ui/label'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

const RichtextField: FC<FieldRendererProps> = ({ field, value, error, onChange }) => {
    const [preview, setPreview] = useState(false)
    const ReactQuill = useMemo(() => dynamic(() => import('react-quill-new'), { ssr: false }), [])
    const isPreviewMode = field?.hidePreview ? false : preview

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='flex justify-between items-center mb-2'>
                <Label htmlFor={field.name}>{field.label}</Label>

                {!field?.hidePreview && (
                    <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setPreview(p => !p)}
                    >
                        {isPreviewMode ? (
                            <>
                                <EyeOffIcon className='h-4 w-4 mr-1' />
                                Edit
                            </>
                        ) : (
                            <>
                                <EyeIcon className='h-4 w-4 mr-1' />
                                Preview
                            </>
                        )}
                    </Button>
                )}
            </div>
            {isPreviewMode && !field?.hidePreview ? (
                <div className='min-h-[500px] p-4 border border-input rounded-md bg-background'>
                    <div
                        className='prose prose-invert max-w-none'
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                (value as string) || '<p>No content to preview</p>'
                            )
                        }}
                    />
                </div>
            ) : (
                <ReactQuill
                    theme='snow'
                    className='bg-inherit text-foreground'
                    value={(value as string) || ''}
                    onChange={(v: string) => onChange(field.name, v, field)}
                    modules={{
                        toolbar: [
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ align: [] }],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ indent: '-1' }, { indent: '+1' }],
                            ['blockquote', 'code-block'],
                            [{ color: [] }, { background: [] }],
                            ['link'],
                            ['clean']
                        ]
                    }}
                />
            )}
        </FieldWrapper>
    )
}

export default RichtextField
